import { useState, useCallback } from "react";
import * as api from "../api/guardias";
import type { Cuadro, Turno, Asignacion, Incidencia, EstadoInc } from "../api/guardias";

/* ────────────────────────────────
   CUADROS (listar/CRUD/publicación)
   ──────────────────────────────── */
export function useGuardiasCuadros() {
  const [rows, setRows] = useState<Cuadro[]>([]);
  const [loading, setL] = useState(false);

  const refresh = useCallback(async () => {
    setL(true);
    try {
      const data = await api.listCuadros();
      setRows(data);
    } finally {
      setL(false);
    }
  }, []);

  /** Crea y devuelve el cuadro creado (además refresca el listado) */
  const create = async (dto: { anio: number; mes: number; nota?: string }) => {
    const created = await api.createCuadro(dto);
    await refresh();
    return created; // ← útil si el llamador lo necesita
  };

  /** Actualiza (solo nota). Coerce: si viene null, lo omitimos para evitar TS error */
  const update = async (id: number, dto: Partial<Pick<Cuadro, "nota">>) => {
    const payload: Partial<{ nota: string }> = {};
    if (typeof dto.nota === "string") payload.nota = dto.nota;
    const updated = await api.updateCuadro(id, payload);
    await refresh();
    return updated;
  };

  const publicar = async (id: number) => {
    const updated = await api.publicarCuadro(id);
    await refresh();
    return updated;
  };

  const archivar = async (id: number) => {
    const updated = await api.archivarCuadro(id);
    await refresh();
    return updated;
  };

  return { rows, loading, refresh, create, update, publicar, archivar };
}

/* ────────────────────────────────
   TURNOS (listar/CRUD/publicación)
   ──────────────────────────────── */
export function useGuardiasTurnos(cuadroId?: number) {
  const [rows, setRows] = useState<Turno[]>([]);
  const [loading, setL] = useState(false);

  const refresh = useCallback(async () => {
    if (!cuadroId) {
      setRows([]);
      return;
    }
    setL(true);
    try {
      const data = await api.listTurnos(cuadroId);
      setRows(data);
    } finally {
      setL(false);
    }
  }, [cuadroId]);

  /** Crea y devuelve el turno creado (además refresca) */
  const create = async (dto: Parameters<typeof api.createTurno>[0]) => {
    const created = await api.createTurno(dto);
    await refresh();
    return created; // ← necesario para publicar inmediatamente después
  };

  const update = async (id: number, dto: Partial<Turno>) => {
    const updated = await api.updateTurno(id, dto);
    await refresh();
    return updated;
  };

  const publicar = async (id: number) => {
    const updated = await api.publicarTurno(id);
    await refresh();
    return updated;
  };

  const cancelar = async (id: number) => {
    const updated = await api.cancelarTurno(id);
    await refresh();
    return updated;
  };

  const remove = async (id: number) => {
    const res = await api.removeTurno(id);
    await refresh();
    return res;
  };

  return { rows, loading, refresh, create, update, publicar, cancelar, remove };
}

/* ────────────────────────────────
   ASIGNACIONES (listar/añadir/liberar)
   ──────────────────────────────── */
export function useGuardiasAsignaciones(turnoId?: number) {
  const [rows, setRows] = useState<Asignacion[]>([]);
  const [loading, setL] = useState(false);

  const refresh = useCallback(async () => {
    if (!turnoId) {
      setRows([]);
      return;
    }
    setL(true);
    try {
      const data = await api.listAsignaciones(turnoId);
      setRows(data);
    } finally {
      setL(false);
    }
  }, [turnoId]);

  const add = async (usuarioId: number) => {
    const r = await api.addAsignacion(Number(turnoId), usuarioId);
    await refresh();
    return r;
  };

  const liberar = async (asigId: number) => {
    const r = await api.liberarAsignacion(asigId);
    await refresh();
    return r;
  };

  const asignadosCount = rows.filter((r) => r.estado === "asignado").length;

  return { rows, loading, refresh, add, liberar, asignadosCount };
}

/* ────────────────────────────────
   INCIDENCIAS (listar/crear/resolver)
   ──────────────────────────────── */
export function useGuardiasIncidencias(
  params: {
    estado?: EstadoInc;
    turnoId?: number;
  } = {}
) {
  const [rows, setRows] = useState<Incidencia[]>([]);
  const [loading, setL] = useState(false);

  const refresh = useCallback(async () => {
    setL(true);
    try {
      const data = await api.listIncidencias(params);
      setRows(data);
    } finally {
      setL(false);
    }
  }, [params.estado, params.turnoId]);

  const crear = async (turnoId: number, tipo: "no_puedo_cubrir" | "otro", mensaje?: string) => {
    const r = await api.crearIncidencia(turnoId, tipo, mensaje);
    await refresh();
    return r;
  };

  const resolver = async (id: number) => {
    const r = await api.resolverIncidencia(id);
    await refresh();
    return r;
  };

  return { rows, loading, refresh, crear, resolver };
}
